import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { shutdownIntercom, bootIntercom } from '../utils/intercom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    // Initialize from localStorage
    try {
      return localStorage.getItem('token');
    } catch (e) {
      console.warn('localStorage not available');
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const logoutRef = useRef(null);

  // Boot Intercom with user data and identity verification
  const bootIntercomWithUser = useCallback(async (userData, authToken) => {
    if (!userData || !authToken) {
      bootIntercom();
      return;
    }
    
    try {
      // Get user hash from backend for identity verification
      const response = await axios.get(`${API}/intercom/user-hash`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const { user_hash, phone } = response.data;
      
      if (window.Intercom) {
        window.Intercom('boot', {
          app_id: process.env.REACT_APP_INTERCOM_APP_ID || 'on5kg6ru',
          email: userData.email,
          name: userData.name,
          user_id: userData.id,
          phone: phone,
          user_hash: user_hash,
          company: userData.company_name ? {
            company_id: userData.id,
            name: userData.company_name
          } : undefined
        });
        console.log('Intercom booted with identity verification for:', userData.email);
      }
    } catch (error) {
      console.error('Failed to boot Intercom with hash, falling back:', error);
      // Fallback without hash
      if (window.Intercom) {
        window.Intercom('boot', {
          app_id: process.env.REACT_APP_INTERCOM_APP_ID || 'on5kg6ru',
          email: userData.email,
          name: userData.name,
          user_id: userData.id
        });
      }
    }
  }, []);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem('token');
    } catch (e) {
      console.warn('Could not clear localStorage');
    }
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    // Reset Intercom to anonymous
    shutdownIntercom();
    bootIntercom();
  }, []);

  // Store logout in ref for interceptor access
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  // Set up axios interceptor for auth header and 401 handling
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }

    // Add response interceptor for 401 errors
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && logoutRef.current) {
          console.log('Token expired or invalid, logging out');
          logoutRef.current();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [token]);

  // Update Intercom when user and token are both available
  useEffect(() => {
    if (user && token) {
      bootIntercomWithUser(user, token);
    }
  }, [user, token, bootIntercomWithUser]);

  // Fetch user on mount if token exists
  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          console.error('Failed to fetch user:', error);
          logout();
        }
      }
      setLoading(false);
    };
    fetchUser();
  }, [token, logout]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    try {
      localStorage.setItem('token', access_token);
    } catch (e) {
      console.warn('Could not persist token to localStorage');
    }
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  };

  const verifyEmail = async (verificationToken) => {
    const response = await axios.post(`${API}/auth/verify-email?token=${verificationToken}`);
    
    // If status is pending_approval, don't auto-login - redirect to pending page
    if (response.data.status === 'pending_approval') {
      return response.data;
    }
    
    // Only set token and user if we got an access_token (active users)
    const { access_token } = response.data;
    if (access_token) {
      try {
        localStorage.setItem('token', access_token);
      } catch (e) {
        console.warn('Could not persist token to localStorage');
      }
      setToken(access_token);
      // Fetch user data
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setUser(userResponse.data);
    }
    return response.data;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    verifyEmail,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
